import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { BodyXS, Box, IconButton, MenuOption, MoreVertIcon, NestedMenu, Skeleton, theme } from '@parspec/pixel';
import { SortProductListRequest } from '../queries/apiTypes';
import { useExportDataMutation, useGetBomDataQuery, useSortProductListMutation } from '../queries';
import { useTrackIntercomEvent } from '../../shared/hooks/useIntercomHook';
import { TAB_VALUES } from './constants';
import Rounding from './Rounding';
import useCreateSectionHook from './hooks/useCreateSectionHook';


interface NestedMenuOptionProps {
    onSortClick: (sortBy: SortProductListRequest) => void;
    isSortDisabled: boolean;
    currentTab: string;
    isCreateOptionDisabled?: boolean;
    onExportClick?: (type: string) => void;
    onClickManageColumns?: () => void;
    notificationCount?: number;
    onClickRounding?: () => void;
    onCreateOption?: () => void
}

const getNestedOptions = ({ onSortClick, isSortDisabled, currentTab, onExportClick, onClickManageColumns, notificationCount, onClickRounding, onCreateOption, isCreateOptionDisabled }: NestedMenuOptionProps) => {
    const options: MenuOption[] = [
        {
            label: 'Export',

            items: [
                { label: 'CSV', callback: () => onExportClick?.('csv') },
                { label: 'Excel', callback: () => onExportClick?.('xls') },
                { label: 'Agile XML', callback: () => onExportClick?.('agile_xml') }
            ]
        },
        {
            label: 'Sort by',
            disabled: isSortDisabled,
            toolTipMessage: !isSortDisabled ? '' : 'Add Item(s) First',
            items: [
                {
                    label: 'Type (A-Z)',
                    callback: () => onSortClick?.({ sorting_field_name: 'category', sorting_order: 'asc' })
                },
                {
                    label: 'Type (Z-A)',
                    callback: () => onSortClick?.({ sorting_field_name: 'category', sorting_order: 'desc' })
                },
                { label: 'Manufacturer (A-Z)', callback: () => onSortClick?.({ sorting_field_name: 'manufacturer', sorting_order: 'asc' }) },
                { label: 'Manufacturer (Z-A)', callback: () => onSortClick?.({ sorting_field_name: 'manufacturer', sorting_order: 'desc' }) }
            ]
        }, {
            label: 'Manage Columns',
            callback: () => onClickManageColumns?.(),
            rightIcon: <Box bgcolor={Boolean(notificationCount) ? 'warning.main' : 'inherit'} borderRadius={50} alignItems={'center'} width={'20px'} height={'20px'} display={'flex'} justifyContent={'center'}>
                <BodyXS color={'neutral.light'} mt={-0.25}>{Boolean(notificationCount) && notificationCount}</BodyXS>
            </Box>,
        },
        {
            label: 'Create Options Table',
            disabled: isCreateOptionDisabled,
            callback: () => onCreateOption?.()
        }
    ];
    if (currentTab === TAB_VALUES.PRICING_AND_LEAD_TIME) {
        options.push({
            label: 'Rounding Behavior',
            callback: () => onClickRounding?.()
        });
    }
    return options;
};
interface KebabNestedMenuProps {
    onClickManageColumn?: () => void;
    isLoading?: boolean;
    hasOption?: boolean
    notificationCount?: number;
    onRoundingSubmit?: (roudingBehviour: string, roudingPrecision: string) => void;
}

export const ANCHOR_ORIGIN = {
    vertical: 'top',
    horizontal: 'left'
} as const;

export const TRANSFROM_ORIGIN = {
    vertical: 'top',
    horizontal: 'right'
} as const;

const KebabNestedMenu = (props: KebabNestedMenuProps) => {
    const { onClickManageColumn, isLoading, notificationCount, onRoundingSubmit, hasOption } = props;
    const location = useLocation();
    const { bomId = '0' } = useParams();

    const { data: bomData } = useGetBomDataQuery(bomId, {
        enabled: Boolean(bomId)
    });
    const { trackIntercomEvent } = useTrackIntercomEvent();

    const { mutateAsync: sortProductList, isLoading: isSortProductListLoading } = useSortProductListMutation();
    const { mutateAsync: exportData, isLoading: exportLoading } = useExportDataMutation();
    const { onCreateSection: handleCreateSection, isLoading: createSectionLoading } = useCreateSectionHook();
    const [isRoundingModelOpen, setRoundingModal] = useState<boolean>(false);

    const tab = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params?.get('tab');
    }, [location.search]);

    const onSort = async (sortOn: SortProductListRequest) => {
        if (!bomId) {
            return;
        }
        trackIntercomEvent('Sort_By');
        await sortProductList({ sortOn, bomId });
    };

    const onExport = async (format: string) => {
        try {
            const data = {
                format,
                bomId: bomId!
            };
            await exportData(data);
        } catch (e) { }
    };

    const onCreateOption = async () => {
        const type = 'options'
        await handleCreateSection(type);
    }

    const onClickManageColumns = () => onClickManageColumn?.();
    const onClosehandle = () => {
        setRoundingModal((old) => !old);
    };

    const onClickRounding = () => setRoundingModal(true);

    const onSubmitRounding = (roudingBehaviour: string, roudingPrecision: string) => {
        onRoundingSubmit?.(roudingBehaviour, roudingPrecision);
        setRoundingModal(false);
    };

    const isActionsLoading = exportLoading || isSortProductListLoading || isLoading || createSectionLoading;
    return (
        <>
            {
                isActionsLoading ? <Skeleton variant="rectangular" height='33px' width='38px' /> : <NestedMenu options={getNestedOptions({ onSortClick: onSort, isSortDisabled: !bomData?.data.length, currentTab: tab || '', onExportClick: onExport, onClickManageColumns, notificationCount, onClickRounding, onCreateOption, isCreateOptionDisabled: hasOption })} transformOrigin={TRANSFROM_ORIGIN as any} anchorOrigin={ANCHOR_ORIGIN as any}>
                    <IconButton sx={{
                        border: '1px solid', borderColor: theme.palette.neutral.dark, height: '36px', borderRadius: '6px', color: 'secondary', '&:hover': {
                            borderColor: theme.palette.secondary.light
                        },
                        '&:disabled': {
                            backgroundColor: theme.palette.neutral.main,
                            opacity: 0.5,
                            borderColor: theme.palette.neutral.dark
                        }
                    }}><MoreVertIcon /></IconButton>
                </NestedMenu>}

            {
                notificationCount ? <Box bgcolor={'warning.main'} position='relative' top={-8} left={52} borderRadius={50} alignItems={'center'} width={'20px'} height={'20px'} display={'flex'} justifyContent={'center'}>
                    <BodyXS color={'neutral.light'} mt={-0.25}>{Boolean(notificationCount) && notificationCount}</BodyXS>
                </Box> : null
            }
            {isRoundingModelOpen && <Rounding open={isRoundingModelOpen} onClose={onClosehandle} onSuccess={onSubmitRounding} />}

        </>
    );
};
export default KebabNestedMenu;
